import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Checkbox,
  Divider,
  message,
  Modal,
  Tabs,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MobileOutlined,
  SafetyOutlined,
  WechatOutlined,
} from '@ant-design/icons';

import { loginAsync } from '../../redux/slices/authSlice';
import authService from '../../services/auth';
import tianaiCaptcha from '../../services/captcha';
import './Login.css';

const { TabPane } = Tabs;

/**
 * 登录页
 * 职责：提供账号密码登录与手机验证码登录两种方式；登录成功后重定向首页。
 * 依赖：Redux `loginAsync` 处理鉴权；`authService.sendVerificationCode` 发送短信验证码；AntD 表单与提示。
 * 状态：`loginType` 当前登录方式；`countdown` 验证码重发倒计时；`loading` 登录请求状态；`isAuthenticated` 全局认证态。
 * 交互：两套表单提交、记住我、忘记密码入口、登录后跳转与 Tab 切换。
 */
const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, isAuthenticated } = useSelector(state => state.auth);

  const [form] = Form.useForm();
  const [phoneForm] = Form.useForm();
  const [loginType, setLoginType] = useState('username'); // 'username' | 'phone'
  const [countdown, setCountdown] = useState(0);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [smsSendInterval, setSmsSendInterval] = useState(60); // 默认60秒

  // 验证码相关状态
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [pendingPurpose, setPendingPurpose] = useState('');
  const [captchaScenario, setCaptchaScenario] = useState('');
  // 触发验证码后暂存登录参数，用于验证通过后重试
  const [pendingLogin, setPendingLogin] = useState(null);

  // 如果已经登录，重定向到首页
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 验证码倒计时
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 获取短信验证码配置（发送间隔等）
  useEffect(() => {
    authService.getCaptchaConfig()
      .then(res => {
        if (res?.data?.smsSendInterval) {
          setSmsSendInterval(res.data.smsSendInterval);
        }
      })
      .catch(err => {
        console.error('获取验证码配置失败:', err);
        // 使用默认值60秒
      });
  }, []);

  // 用户名密码登录：分发 Redux 异步动作完成鉴权
  const handleUsernameLogin = async (values) => {
    try {
      await dispatch(loginAsync({
        loginType: 'username',
        username: values.username,
        password: values.password,
        rememberMe: values.rememberMe,
      })).unwrap();

      message.success('登录成功');
      navigate('/', { replace: true });
    } catch (error) {
      if (error?.data?.captchaRequired) {
        setPendingLogin({
          loginType: 'username',
          username: values.username,
          password: values.password,
          rememberMe: values.rememberMe,
        });
        setCaptchaScenario('login');
        setCaptchaVisible(true);
        message.warning(error?.message || '需要完成人机验证');
        return;
      }
      message.error(error?.message || '登录失败');
    }
  };

  // 手机号验证码登录：分发 Redux 异步动作完成鉴权
  const handlePhoneLogin = async (values) => {
    try {
      await dispatch(loginAsync({
        loginType: 'phone',
        phone: values.phone,
        verificationCode: values.verificationCode,
        rememberMe: values.rememberMe,
      })).unwrap();

      message.success('登录成功');
      navigate('/', { replace: true });
    } catch (error) {
      message.error(error.message || '登录失败');
    }
  };

  // 发送验证码：校验手机号后触发短信发送，并开启倒计时
  const handleSendCode = async () => {
    try {
      const phone = phoneForm.getFieldValue('phone');
      if (!phone) {
        message.error('请输入手机号');
        return;
      }

      // 调用发送验证码的API
      const res = await authService.sendVerificationCode(phone, 'login');
      const data = res?.data;
      if (data?.captchaRequired && !data?.sent) {
        // 需要完成人机验证，打开验证码弹窗
        setPendingPhone(phone);
        setPendingPurpose('login');
        setCaptchaScenario('sms');
        setCaptchaVisible(true);
        message.warning(res?.message || '需要完成人机验证');
        return;
      }
      message.success('验证码已发送');
      setCountdown(smsSendInterval);
    } catch (error) {
      message.error(error.message || '发送验证码失败');
    }
  };

  // 处理验证码验证成功
  const handleCaptchaSuccess = async (token) => {
    try {
      if (captchaScenario === 'login' && pendingLogin) {
        await dispatch(loginAsync({
          ...pendingLogin,
          captchaToken: token,
        })).unwrap();
        message.success('登录成功');
        setPendingLogin(null);
        setCaptchaScenario('');
        navigate('/', { replace: true });
        return;
      }

      const res = await authService.sendVerificationCode(pendingPhone, pendingPurpose, token);
      const responseData = res?.data;

      if (responseData?.sent) {
        message.success('验证码已发送');
        setCountdown(smsSendInterval);
        setCaptchaVisible(false);
      } else {
        message.error(responseData?.message || '发送验证码失败');
      }
    } catch (error) {
      if (captchaScenario === 'login' && error?.data?.captchaRequired) {
        setCaptchaVisible(true);
        message.warning(error?.message || '需要完成人机验证');
        return;
      }
      if (captchaScenario === 'login') {
        message.error(error?.message || '登录失败');
        return;
      }
      message.error(error?.message || '发送验证码失败');
    }
  };

  const handleWechatLogin = async () => {
    setWechatLoading(true);
    try {
      const redirectUri = `${window.location.origin}/auth/wechat/callback`;
      const response = await authService.getWechatAuthorizeUrl(redirectUri, '/');
      const url = response?.data?.url;
      if (!url) {
        message.error('获取微信登录地址失败，请稍后重试');
        return;
      }
      window.location.href = url;
    } catch (error) {
      message.error(error?.message || '微信登录失败');
    } finally {
      setWechatLoading(false);
    }
  };

  // 验证码弹窗
  const captchaModal = (
    <Modal
      open={captchaVisible}
      title="安全验证"
      onCancel={() => setCaptchaVisible(false)}
      footer={null}
      destroyOnClose
      centered
    >
      <div id="tianai-captcha-box" style={{ minWidth: 318, minHeight: 318 }} />
    </Modal>
  );

  // 当验证码弹窗打开时，初始化验证码
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!captchaVisible) return;

    let disposed = false;
    const timer = setTimeout(() => {
      if (disposed) return;
      tianaiCaptcha.show({
        bindEl: '#tianai-captcha-box',
        onValidSuccess: (token, tac) => {
          handleCaptchaSuccess(token);
          tac.destroyWindow();
          setCaptchaVisible(false);
        },
        onValidFail: () => {
          message.error('验证失败，请重试');
        }
      }).catch((error) => {
        // console.error('验证码初始化失败:', error);
        message.error(error?.message || '验证码初始化失败');
        setCaptchaVisible(false);
      });
    }, 0);

    return () => {
      disposed = true;
      clearTimeout(timer);
      tianaiCaptcha.destroy();
    };
  }, [captchaVisible]);

  // 账号密码登录表单
  const renderUsernameLogin = () => (
    <Form
      form={form}
      name="username-login"
      onFinish={handleUsernameLogin}
      autoComplete="off"
      size="large"
    >
      <Form.Item
        name="username"
        rules={[
          { required: true, message: '请输入用户名或手机号' },
          { min: 2, message: '用户名至少2个字符' },
        ]}
      >
        <Input
          prefix={<UserOutlined />}
          placeholder="用户名或手机号"
          autoComplete="username"
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          { required: true, message: '请输入密码' },
          { min: 6, message: '密码至少6个字符' },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="密码"
          autoComplete="current-password"
        />
      </Form.Item>

      <Form.Item>
        <div className="login-options">
          <Form.Item name="rememberMe" valuePropName="checked" noStyle>
            <Checkbox>记住我</Checkbox>
          </Form.Item>
          <Link to="/auth/forgot-password" className="forgot-password">
            忘记密码？
          </Link>
        </div>
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          block
          className="login-button"
        >
          登录
        </Button>
      </Form.Item>
    </Form>
  );

  // 手机号验证码登录表单
  const renderPhoneLogin = () => (
    <Form
      form={phoneForm}
      name="phone-login"
      onFinish={handlePhoneLogin}
      autoComplete="off"
      size="large"
    >
      <Form.Item
        name="phone"
        rules={[
          { required: true, message: '请输入手机号' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
        ]}
      >
        <Input
          prefix={<MobileOutlined />}
          placeholder="手机号"
          autoComplete="tel"
        />
      </Form.Item>

      <Form.Item
        name="verificationCode"
        rules={[
          { required: true, message: '请输入验证码' },
          { len: 6, message: '验证码为6位数字' },
        ]}
      >
        <div className="verification-input">
          <Input
            prefix={<SafetyOutlined />}
            placeholder="验证码"
            autoComplete="one-time-code"
          />
          <Button
            type="default"
            onClick={handleSendCode}
            disabled={countdown > 0}
            className="send-code-button"
          >
            {countdown > 0 ? `${countdown}s` : '发送验证码'}
          </Button>
        </div>
      </Form.Item>

      <Form.Item>
        <Form.Item name="rememberMe" valuePropName="checked" noStyle>
          <Checkbox>记住我</Checkbox>
        </Form.Item>
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          block
          className="login-button"
        >
          登录
        </Button>
      </Form.Item>
    </Form>
  );

  return (
    <div className="login-container">
      <Tabs
        activeKey={loginType}
        onChange={setLoginType}
        centered
        className="login-tabs"
      >
        <TabPane tab="账号登录" key="username">
          {renderUsernameLogin()}
        </TabPane>
        <TabPane tab="手机登录" key="phone">
          {renderPhoneLogin()}
        </TabPane>
      </Tabs>

      <Divider plain>其他登录方式</Divider>

      <div className="other-login">
        <Button
          type="default"
          block
          disabled
          icon={<WechatOutlined style={{ color: '#07C160' }} />}
        >
          微信登录（即将开放）
        </Button>
      </div>

      <div className="register-link">
        还没有账号？
        <Link to="/auth/register">立即注册</Link>
      </div>

      {captchaModal}
    </div>
  );
};

export default Login;
